import { abs } from "@suse-o11y/abs";
import { secondsInHour, secondsInMinute } from "@suse-o11y/date-time";
import { floor } from "@suse-o11y/floor";
import { max } from "@suse-o11y/max";
import type { Record } from "@suse-o11y/record";

import type { PrecisionCount } from "./types";
import { toFixed } from "./value-formats";

const UNITS = [
	"nanosecond",
	"microsecond",
	"millisecond",
	"second",
	"minute",
	"hour",
	"day",
	"week",
	"month",
	"year",
] as const;

export type TimeUnit = (typeof UNITS)[number];

const TIME_UNITS_SUFFIX: Record<TimeUnit, string> = {
	nanosecond: "ns",
	microsecond: "µs",
	millisecond: "ms",
	second: "s",
	minute: "min",
	hour: "hour",
	day: "day",
	week: "week",
	month: "month",
	year: "year",
};

const TIME_UNITS_TO_SECONDS: Record<TimeUnit, number> = {
	nanosecond: 1e-9,
	microsecond: 1e-6,
	millisecond: 1e-3,
	second: 1,
	minute: secondsInMinute,
	hour: secondsInHour,
	day: secondsInHour * 24,
	week: secondsInHour * 24 * 7,
	month: secondsInHour * 24 * 30,
	year: secondsInHour * 24 * 365,
};

interface FindSuitableTimeUnitsOptions {
	minimalUnit: TimeUnit;
	maximumUnit: TimeUnit;
}

const findSuitableTimeUnits = (
	seconds: number,
	{ minimalUnit, maximumUnit }: FindSuitableTimeUnitsOptions,
): TimeUnit => {
	for (let i = UNITS.indexOf(minimalUnit) + 1; i <= UNITS.indexOf(maximumUnit); i++) {
		const unit = UNITS[i];
		const unitAsSeconds = TIME_UNITS_TO_SECONDS[unit];

		if (abs(seconds) < unitAsSeconds) {
			return UNITS[i - 1];
		}
	}

	return maximumUnit;
};

export const convertTimeFrom = (
	fromUnit: TimeUnit,
	count: number,
	precision?: PrecisionCount,
	{
		minimalUnit = fromUnit,
		maximumUnit = "year",
		trimTrailingZeros = false,
		trimZeroUnits = false,
	}: {
		minimalUnit?: TimeUnit;
		maximumUnit?: TimeUnit;
		/**
		 * @default false
		 */
		trimTrailingZeros?: boolean;
		/**
		 * @default false
		 */
		trimZeroUnits?: boolean;
	} = {},
): string => {
	const precision_ = max(0, precision ?? 0);

	if (count === 0) {
		return trimZeroUnits ? "0" : `0${TIME_UNITS_SUFFIX[fromUnit]}`;
	}

	const seconds = count * TIME_UNITS_TO_SECONDS[fromUnit];
	const suitableUnits = findSuitableTimeUnits(seconds, { minimalUnit, maximumUnit });
	const suffix = TIME_UNITS_SUFFIX[suitableUnits];

	if (
		suitableUnits === "second" ||
		suitableUnits === "millisecond" ||
		suitableUnits === "microsecond" ||
		suitableUnits === "nanosecond"
	) {
		const value = toFixed(seconds / TIME_UNITS_TO_SECONDS[suitableUnits], precision_);

		// trim trailing trailing zeros with a dot
		if (trimTrailingZeros && value.includes(".")) {
			return `${value.replace(/\.?0+$/u, "")}${suffix}`;
		}

		return `${value}${suffix}`;
	}

	const whole = floor(seconds / TIME_UNITS_TO_SECONDS[suitableUnits], 0);

	if (precision_ === 0 || UNITS.indexOf(suitableUnits) >= UNITS.indexOf(maximumUnit)) {
		return `${whole}${suffix}`;
	}

	// formats the units higher than second (minute, hour, day, week, month, year),
	// it includes as many next units as precision is set
	const lower = convertTimeFrom("second", seconds - whole * TIME_UNITS_TO_SECONDS[suitableUnits], precision_ - 1, {
		trimTrailingZeros: true,
		trimZeroUnits: true,
	});

	// do not include trailing 0
	return lower === "0" ? `${whole}${suffix}` : `${whole}${suffix} ${lower}`;
};
